/**
 * TextEditor Component
 *
 * WYSIWYG text editor with AI refinement options.
 * Supports bold, italic, underline formatting.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { EditableElement } from '../types';
import { useRenderer } from '../context';
import { refineText, type RefinementType } from '../../api/staging';

// ============================================================================
// Types
// ============================================================================

interface TextEditorProps {
  element: EditableElement;
  websiteId: string;
  onClose: () => void;
}

// ============================================================================
// WYSIWYG Toolbar Button
// ============================================================================

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded transition-colors ${
        active
          ? 'bg-primary-100 text-primary-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

// ============================================================================
// AI Refinement Button
// ============================================================================

interface RefinementButtonProps {
  type: RefinementType;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  loading: boolean;
  activeType: RefinementType | null;
}

function RefinementButton({ type, label, icon, onClick, loading, activeType }: RefinementButtonProps) {
  const isActive = loading && activeType === type;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
        isActive
          ? 'bg-primary-50 border-primary-300 text-primary-700'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
      } ${loading && !isActive ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {isActive ? (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        icon
      )}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

// ============================================================================
// TextEditor Component
// ============================================================================

export function TextEditor({ element, websiteId, onClose }: TextEditorProps) {
  const { stageChange, getValue, revertChange, stagedChanges } = useRenderer();

  // Get current value (might be staged)
  const currentValue = getValue(element.id, element.value);
  const textValue = Array.isArray(currentValue) ? currentValue.join('\n') : currentValue;

  // Local state for editing
  const [content, setContent] = useState(textValue);
  const [isRefining, setIsRefining] = useState(false);
  const [activeRefinement, setActiveRefinement] = useState<RefinementType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const hasChanges = content !== textValue;
  const hasUnsavedChanges = element.id in stagedChanges;

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = textValue;
    }
  }, []);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  }, []);

  // Format commands
  const execCommand = useCallback((command: string) => {
    document.execCommand(command, false);
    handleInput();
    editorRef.current?.focus();
  }, [handleInput]);

  // Check if format is active
  const isFormatActive = useCallback((command: string) => {
    return document.queryCommandState(command);
  }, []);

  // Handle AI refinement
  const handleRefine = useCallback(async (type: RefinementType) => {
    if (!editorRef.current) return;

    setIsRefining(true);
    setActiveRefinement(type);
    setError(null);

    try {
      // Get plain text for refinement (strip HTML)
      const plainText = editorRef.current.innerText || editorRef.current.textContent || '';

      const result = await refineText(
        websiteId,
        plainText,
        type,
        element.label
      );

      if (result.success) {
        // Update the editor content with refined text
        // Preserve basic formatting if the text structure is similar
        editorRef.current.innerHTML = result.refined;
        setContent(result.refined);
      }
    } catch (err) {
      console.error('Failed to refine text:', err);
      setError('Failed to refine text. Please try again.');
    } finally {
      setIsRefining(false);
      setActiveRefinement(null);
    }
  }, [websiteId, element.label]);

  // Save changes
  const handleSave = useCallback(() => {
    if (!editorRef.current) return;

    const newValue = editorRef.current.innerHTML;
    stageChange(element.id, element.value, newValue);
    onClose();
  }, [element, stageChange, onClose]);

  // Revert to original
  const handleRevert = useCallback(() => {
    if (editorRef.current) {
      // Revert to the original element value (not the staged value)
      const originalValue = Array.isArray(element.value) ? element.value.join('\n') : element.value;
      editorRef.current.innerHTML = originalValue;
      setContent(originalValue);

      // Remove from staged changes if present
      if (hasUnsavedChanges) {
        revertChange(element.id);
      }
    }
  }, [element.value, hasUnsavedChanges, revertChange]);

  return (
    <div className="space-y-4">
      {/* WYSIWYG Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
        <ToolbarButton
          onClick={() => execCommand('bold')}
          active={isFormatActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => execCommand('italic')}
          active={isFormatActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0l-2 16m-2 0h4" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => execCommand('underline')}
          active={isFormatActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 4v7a5 5 0 0 0 10 0V4M5 20h14" />
          </svg>
        </ToolbarButton>

        <div className="w-px h-6 bg-gray-300 mx-2" />

        <ToolbarButton
          onClick={() => execCommand('removeFormat')}
          title="Remove Formatting"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </ToolbarButton>
      </div>

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[150px] max-h-[300px] overflow-y-auto p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        style={{ whiteSpace: 'pre-wrap' }}
      />

      {/* AI Refinement Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">AI Refinement</span>
        </div>

        <div className="flex flex-wrap gap-2">
          <RefinementButton
            type="shorter"
            label="Shorter"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            }
            onClick={() => handleRefine('shorter')}
            loading={isRefining}
            activeType={activeRefinement}
          />

          <RefinementButton
            type="longer"
            label="Longer"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
            onClick={() => handleRefine('longer')}
            loading={isRefining}
            activeType={activeRefinement}
          />

          <RefinementButton
            type="friendlier"
            label="Friendlier"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            onClick={() => handleRefine('friendlier')}
            loading={isRefining}
            activeType={activeRefinement}
          />

          <RefinementButton
            type="professional"
            label="Professional"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
            onClick={() => handleRefine('professional')}
            loading={isRefining}
            activeType={activeRefinement}
          />

          <RefinementButton
            type="regenerate"
            label="Regenerate"
            icon={
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
            onClick={() => handleRefine('regenerate')}
            loading={isRefining}
            activeType={activeRefinement}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          {(hasChanges || hasUnsavedChanges) && (
            <button
              type="button"
              onClick={handleRevert}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Revert to original
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges && !hasUnsavedChanges}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              hasChanges || hasUnsavedChanges
                ? 'bg-primary-600 hover:bg-primary-700'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
